import React, { useState, useEffect, useCallback, useRef } from 'react';
import './AnnouncementManager.css';
import api from './api'; // Import the API service
import { usePermissions } from '../../hooks/usePermissions';

const AnnouncementPage = () => {
    const permissions = usePermissions('Announcements');
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [shopId, setShopId] = useState('');
    const [shops, setShops] = useState([]);
    const [uploadingFile, setUploadingFile] = useState(false);

    // NEW: Mode toggle state
    const [mode, setMode] = useState('KDP'); // 'KDP', 'KD', 'DELIVERY', or 'SHOP_MEMBERS'
    const [userId, setUserId] = useState(''); // For KD mode
    const [users, setUsers] = useState([]); // For KD mode user list
    const [deliveryUserId, setDeliveryUserId] = useState('');
    const [deliveryBoys, setDeliveryBoys] = useState([]);
    const [deliveryShopId, setDeliveryShopId] = useState('');
    const [shopMemberUserId, setShopMemberUserId] = useState('');
    const [shopMembers, setShopMembers] = useState([]);

    // File references
    const imageInputRef = useRef(null);
    const videoInputRef = useRef(null);

    // Form states
    const [formMode, setFormMode] = useState('create');
    const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        type: 'text',
        text: '',
        url: '',
        shopId: '',
        userId: '',
        deliveryUserId: '',
        shopMemberUserId: '',
        forAll: false,
        isActive: true
    });

    // Preview states
    const [previewUrl, setPreviewUrl] = useState('');

    // NEW: Fetch users for KD mode
    const fetchUsers = useCallback(async () => {
        try {
            const usersData = await api.getUsers();
            setUsers(usersData);

            // Set default user
            if (usersData.length > 0 && !userId) {
                setUserId(usersData[0]._id);
            }
        } catch (err) {
            setError('Failed to fetch users: ' + (err.response?.data?.error || err.message));
        }
    }, [userId]);

    // NEW: Fetch delivery boys for DELIVERY mode
    const fetchDeliveryBoys = useCallback(async (selectedShopId) => {
        if (!selectedShopId) return;

        try {
            const deliveryData = await api.getDeliveryBoys(selectedShopId);
            setDeliveryBoys(deliveryData);

            // Set default delivery user
            if (deliveryData.length > 0 && !deliveryUserId) {
                setDeliveryUserId(deliveryData[0]._id);
            }
        } catch (err) {
            setError('Failed to fetch delivery boys: ' + (err.response?.data?.error || err.message));
        }
    }, [deliveryUserId]);

    // NEW: Fetch shop members for SHOP_MEMBERS mode
    const fetchShopMembers = useCallback(async () => {
        try {
            const shopMembersData = await api.getUsers(); // Using same users API as KD mode
            setShopMembers(shopMembersData);

            // Set default shop member user
            if (shopMembersData.length > 0 && !shopMemberUserId) {
                setShopMemberUserId(shopMembersData[0]._id);
            }
        } catch (err) {
            setError('Failed to fetch shop members: ' + (err.response?.data?.error || err.message));
        }
    }, [shopMemberUserId]);

    // Fetch shops
    const fetchShops = useCallback(async () => {
        try {
            const shopsData = await api.getShops();
            setShops(shopsData);

            // Set default shop
            if (shopsData.length > 0 && !shopId) {
                setShopId(shopsData[0]._id);
            }
        } catch (err) {
            setError('Failed to fetch shops: ' + (err.response?.data?.error || err.message));
        }
    }, [shopId]);

    // UPDATED: Fetch announcements based on mode
    const fetchAnnouncements = useCallback(async () => {
        try {
            setLoading(true);

            if (mode === 'KDP') {
                if (!shopId) return;
                const data = await api.getAnnouncements(shopId);
                setAnnouncements(data);
            } else if (mode === 'KD') {
                if (!userId) return;
                const data = await api.getUserAnnouncements(userId);
                setAnnouncements(data);
            } else if (mode === 'DELIVERY') {
                if (!deliveryUserId) return;
                const data = await api.getDeliveryAnnouncements(deliveryUserId);
                setAnnouncements(data);
            } else if (mode === 'SHOP_MEMBERS') {
                if (!shopMemberUserId) return;
                const data = await api.getShopMemberAnnouncements(shopMemberUserId);
                setAnnouncements(data);
            }

            setLoading(false);
        } catch (err) {
            setError('Failed to fetch announcements: ' + (err.response?.data?.error || err.message));
            setLoading(false);
        }
    }, [mode, shopId, userId, deliveryUserId, shopMemberUserId]);

    useEffect(() => {
        fetchShops();
        if (mode === 'KD') {
            fetchUsers();
        } else if (mode === 'DELIVERY' && deliveryShopId) {
            fetchDeliveryBoys(deliveryShopId);
        } else if (mode === 'SHOP_MEMBERS') {
            fetchShopMembers();
        }
    }, [fetchShops, fetchUsers, fetchDeliveryBoys, fetchShopMembers, mode, deliveryShopId]);

    useEffect(() => {
        if ((mode === 'KDP' && shopId) || (mode === 'KD' && userId) || (mode === 'DELIVERY' && deliveryUserId) || (mode === 'SHOP_MEMBERS' && shopMemberUserId)) {
            fetchAnnouncements();
        }
    }, [mode, shopId, userId, deliveryUserId, shopMemberUserId, fetchAnnouncements]);

    // NEW: Handle mode change
    const handleModeChange = (newMode) => {
        setMode(newMode);
        setAnnouncements([]);
        setError(null);
        setDeliveryShopId(''); // Reset delivery shop selection
        setDeliveryBoys([]); // Clear delivery boys list
        setDeliveryUserId(''); // Clear selected delivery user
        setShopMemberUserId(''); // Clear selected shop member user
        setShopMembers([]); // Clear shop members list
        resetForm();
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Special handling for forAll - if true, clear shopId/userId/deliveryUserId in the form
        if (name === 'forAll' && checked === true) {
            setFormData({
                ...formData,
                forAll: true,
                shopId: '', // Clear shopId when forAll is true
                userId: '',  // Clear userId when forAll is true
                deliveryUserId: '', // Clear deliveryUserId when forAll is true
                shopMemberUserId: '' // Clear shopMemberUserId when forAll is true
            });
        } else if (name === 'forAll' && checked === false) {
            setFormData({
                ...formData,
                forAll: false,
                shopId: mode === 'KDP' ? shopId : '', // Set current shopId when forAll is false for KDP
                userId: mode === 'KD' ? userId : mode === 'SHOP_MEMBERS' ? shopMemberUserId : '',   // Set current userId when forAll is false for KD or SHOP_MEMBERS
                deliveryUserId: mode === 'DELIVERY' ? deliveryUserId : '', // Set current deliveryUserId when forAll is false for DELIVERY
                shopMemberUserId: mode === 'SHOP_MEMBERS' ? shopMemberUserId : '' // Set current shopMemberUserId when forAll is false for SHOP_MEMBERS
            });
        } else if (name === 'type') {
            // When changing type, clear URL or text accordingly
            if (value === 'text') {
                setFormData({
                    ...formData,
                    type: value,
                    url: ''
                });
                setPreviewUrl('');
            } else {
                setFormData({
                    ...formData,
                    type: value,
                    text: ''
                });
            }
        } else {
            setFormData({
                ...formData,
                [name]: type === 'checkbox' ? checked : value
            });
        }
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            setUploadingFile(true);
            setError(null);

            // Resolve shopId and userId required by upload API (from current mode and form)
            const effectiveShopId = formData.shopId || (mode === 'DELIVERY' ? deliveryShopId : shopId) || (shops[0]?._id);
            let effectiveUserId = formData.userId || (mode === 'KD' ? userId : mode === 'DELIVERY' ? deliveryUserId : mode === 'SHOP_MEMBERS' ? shopMemberUserId : '');
            if (!effectiveUserId) {
                try {
                    const profile = JSON.parse(localStorage.getItem('kd_admin_employee_profile') || '{}');
                    effectiveUserId = profile._id || profile.id || '';
                } catch (_) {}
            }
            if (!effectiveShopId || !effectiveUserId) {
                setError('File upload failed: Please select a shop and ensure user context is set (e.g. select a user in KD/Delivery/Shop Members mode, or refresh and try again).');
                setUploadingFile(false);
                return;
            }

            const uploadParams = { shopId: effectiveShopId, userId: effectiveUserId };
            // Detect type from form context or file MIME type so video is never treated as image
            const isVideo = formData.type === 'video' || (file.type && file.type.startsWith('video/'));

            let result;
            if (isVideo) {
                result = await api.uploadVideo(file, uploadParams);
            } else {
                result = await api.uploadImage(file, uploadParams);
            }

            const urlData = result?.file_url ?? result?.file_urls;
            const fileURL = Array.isArray(urlData) ? urlData[0] : urlData;
            if (result?.success && fileURL) {
                setFormData((prev) => ({
                    ...prev,
                    url: fileURL
                }));
                setPreviewUrl(isVideo ? URL.createObjectURL(file) : fileURL);
            } else {
                throw new Error(result?.error || 'Upload failed');
            }
        } catch (err) {
            const message = err.response?.data?.error || err.message || 'Unknown error';
            setError('File upload failed: ' + message);
        } finally {
            setUploadingFile(false);
            e.target.value = '';
        }
    };

    const handleUploadClick = (type) => {
        if (type === 'image') {
            imageInputRef.current?.click();
        } else if (type === 'video') {
            videoInputRef.current?.click();
        }
    };

    const resetForm = () => {
        setFormData({
            title: '',
            type: 'text',
            text: '',
            url: '',
            shopId: formData.forAll ? '' : (mode === 'KDP' ? shopId : ''),
            userId: formData.forAll ? '' : (mode === 'KD' ? userId : mode === 'SHOP_MEMBERS' ? shopMemberUserId : ''),
            deliveryUserId: formData.forAll ? '' : (mode === 'DELIVERY' ? deliveryUserId : ''),
            shopMemberUserId: formData.forAll ? '' : (mode === 'SHOP_MEMBERS' ? shopMemberUserId : ''),
            forAll: false,
            isActive: true
        });
        setFormMode('create');
        setSelectedAnnouncement(null);
        setPreviewUrl('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        try {
            // Prepare data for submission
            const submissionData = { ...formData };

            if (mode === 'KDP') {
                // Only include shopId if not forAll
                if (submissionData.forAll) {
                    delete submissionData.shopId;
                } else if (!submissionData.shopId) {
                    submissionData.shopId = shopId;
                }
                delete submissionData.userId; // Remove userId for KDP mode
                delete submissionData.deliveryUserId; // Remove deliveryUserId for KDP mode
                delete submissionData.shopMemberUserId; // Remove shopMemberUserId for KDP mode
            } else if (mode === 'KD') {
                // Only include userId if not forAll
                if (submissionData.forAll) {
                    delete submissionData.userId;
                } else if (!submissionData.userId) {
                    submissionData.userId = userId;
                }
                delete submissionData.shopId; // Remove shopId for KD mode
                delete submissionData.deliveryUserId; // Remove deliveryUserId for KD mode
                delete submissionData.shopMemberUserId; // Remove shopMemberUserId for KD mode
            } else if (mode === 'DELIVERY') {
                // Only include deliveryUserId if not forAll
                if (submissionData.forAll) {
                    delete submissionData.deliveryUserId;
                } else if (!submissionData.deliveryUserId) {
                    submissionData.deliveryUserId = deliveryUserId;
                }
                delete submissionData.shopId; // Remove shopId for DELIVERY mode
                delete submissionData.userId; // Remove userId for DELIVERY mode
                delete submissionData.shopMemberUserId; // Remove shopMemberUserId for DELIVERY mode
            } else if (mode === 'SHOP_MEMBERS') {
                // Only include userId (from shopMemberUserId) if not forAll
                if (submissionData.forAll) {
                    delete submissionData.userId;
                } else if (!submissionData.userId) {
                    submissionData.userId = shopMemberUserId; // Use shopMemberUserId as userId
                }
                delete submissionData.shopId; // Remove shopId for SHOP_MEMBERS mode
                delete submissionData.shopMemberUserId; // Remove shopMemberUserId for SHOP_MEMBERS mode
                delete submissionData.deliveryUserId; // Remove deliveryUserId for SHOP_MEMBERS mode
            }

            if (formMode === 'create') {
                if (mode === 'KDP') {
                    await api.createAnnouncement(submissionData);
                } else if (mode === 'KD') {
                    await api.createUserAnnouncement(submissionData);
                } else if (mode === 'DELIVERY') {
                    await api.createDeliveryAnnouncement(submissionData);
                } else if (mode === 'SHOP_MEMBERS') {
                    await api.createShopMemberAnnouncement(submissionData);
                }
            } else if (formMode === 'edit' && selectedAnnouncement) {
                if (mode === 'KDP') {
                    await api.updateAnnouncement(selectedAnnouncement._id, submissionData);
                } else if (mode === 'KD') {
                    await api.updateUserAnnouncement(selectedAnnouncement._id, submissionData);
                } else if (mode === 'DELIVERY') {
                    await api.updateDeliveryAnnouncement(selectedAnnouncement._id, submissionData);
                } else if (mode === 'SHOP_MEMBERS') {
                    await api.updateShopMemberAnnouncement(selectedAnnouncement._id, submissionData);
                }
            }

            resetForm();
            fetchAnnouncements();
        } catch (err) {
            setError('Operation failed: ' + (err.response?.data?.error || err.message));
        }
    };

    const handleEdit = (announcement) => {
        setSelectedAnnouncement(announcement);
        setFormData({
            title: announcement.title || '',
            type: announcement.type,
            text: announcement.text || '',
            url: announcement.url || '',
            shopId: announcement.shopId || '',
            userId: announcement.userId || '',
            deliveryUserId: announcement.deliveryUserId || '',
            shopMemberUserId: announcement.shopMemberUserId || '',
            forAll: announcement.forAll,
            isActive: announcement.isActive
        });

        // Set preview if it's an image or video
        if ((announcement.type === 'image' || announcement.type === 'video') && announcement.url) {
            setPreviewUrl(announcement.url);
        } else {
            setPreviewUrl('');
        }

        setFormMode('edit');
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this announcement?')) return;

        try {
            if (mode === 'KDP') {
                await api.deleteAnnouncement(id);
            } else if (mode === 'KD') {
                await api.deleteUserAnnouncement(id);
            } else if (mode === 'DELIVERY') {
                await api.deleteDeliveryAnnouncement(id);
            } else if (mode === 'SHOP_MEMBERS') {
                await api.deleteShopMemberAnnouncement(id);
            }
            fetchAnnouncements();
        } catch (err) {
            setError('Delete failed: ' + (err.response?.data?.error || err.message));
        }
    };

    const handleToggleActive = async (id, currentStatus) => {
        try {
            if (mode === 'KDP') {
                await api.toggleAnnouncementActive(id);
            } else if (mode === 'KD') {
                await api.toggleUserAnnouncementActive(id);
            } else if (mode === 'DELIVERY') {
                await api.toggleDeliveryAnnouncementActive(id);
            } else if (mode === 'SHOP_MEMBERS') {
                await api.toggleShopMemberAnnouncementActive(id);
            }

            // Update local state without refetching
            setAnnouncements(announcements.map(ann =>
                ann._id === id ? { ...ann, isActive: !currentStatus } : ann
            ));
        } catch (err) {
            setError('Toggle failed: ' + (err.response?.data?.error || err.message));
        }
    };

    // Group announcements by type (for all vs. shop/user specific)
    const globalAnnouncements = announcements.filter(ann => ann.forAll);
    const specificAnnouncements = announcements.filter(ann => !ann.forAll);

    return (
        <div className="announcement-page">
            <div className="announcement-page-header">
                <h1>Announcement Management</h1>

                <div className="mode-toggle">
                    <button
                        type="button"
                        className={`mode-btn ${mode === 'KDP' ? 'active' : ''}`}
                        onClick={() => handleModeChange('KDP')}
                    >
                        KDP
                    </button>
                    <button
                        type="button"
                        className={`mode-btn ${mode === 'KD' ? 'active' : ''}`}
                        onClick={() => handleModeChange('KD')}
                    >
                        KD
                    </button>
                    <button
                        type="button"
                        className={`mode-btn ${mode === 'DELIVERY' ? 'active' : ''}`}
                        onClick={() => handleModeChange('DELIVERY')}
                    >
                        DELIVERY
                    </button>
                    <button
                        type="button"
                        className={`mode-btn ${mode === 'SHOP_MEMBERS' ? 'active' : ''}`}
                        onClick={() => handleModeChange('SHOP_MEMBERS')}
                    >
                        SHOP MEMBERS
                    </button>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            {/* UPDATED: Conditional Shop/User/Delivery Selector */}
            {mode === 'KDP' ? (
                <div className="shop-selector">
                    <label>Select Shop: </label>
                    <select
                        value={shopId}
                        onChange={(e) => setShopId(e.target.value)}
                        className="shop-dropdown"
                    >
                        <option value="" disabled>
                            -- Select Shop --
                        </option>
                        {shops.map(shop => (
                            <option key={shop._id} value={shop._id}>
                                {shop.name}
                            </option>
                        ))}
                    </select>

                    {shopId && (
                        <button onClick={() => setShopId("")} className="clear-button">
                            Clear Selection
                        </button>
                    )}
                </div>
            ) : mode === 'KD' ? (
                <div className="user-selector">
                    <label>Select User: </label>
                    <select
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        className="user-dropdown"
                    >
                        <option value="" disabled>
                            -- Select User --
                        </option>
                        {users.map(user => (
                            <option key={user._id} value={user._id}>
                                {user.fullName ? user.fullName : "No Name"}, PH: +91 {user.phone}
                            </option>
                        ))}
                    </select>

                    {userId && (
                        <button onClick={() => setUserId("")} className="clear-button">
                            Clear Selection
                        </button>
                    )}
                </div>
            ) : mode === 'DELIVERY' ? (
                <div className="delivery-selector">
                    <div className="selector-row">
                        <label>Select Shop:</label>
                        <select
                            value={deliveryShopId}
                            onChange={(e) => {
                                setDeliveryShopId(e.target.value);
                                setDeliveryUserId('');
                                setDeliveryBoys([]);
                            }}
                            className="shop-dropdown"
                        >
                            <option value="" disabled>
                                -- Select Shop --
                            </option>
                            {shops.map(shop => (
                                <option key={shop._id} value={shop._id}>
                                    {shop.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {deliveryShopId && (
                        <div className="selector-row">
                            <label>Select Delivery Boy:</label>
                            <select
                                value={deliveryUserId}
                                onChange={(e) => setDeliveryUserId(e.target.value)}
                                className="delivery-dropdown"
                            >
                                <option value="" disabled>
                                    -- Select Delivery Boy --
                                </option>
                                {deliveryBoys.map(deliveryBoy => (
                                    <option key={deliveryBoy._id} value={deliveryBoy._id}>
                                        {deliveryBoy.fullName ? deliveryBoy.fullName : "No Name"}, PH: +91 {deliveryBoy.phone}
                                    </option>
                                ))}
                            </select>

                            {deliveryUserId && (
                                <button type="button" onClick={() => setDeliveryUserId("")} className="clear-button">
                                    Clear Selection
                                </button>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <div className="shop-member-selector">
                    <label>Select Shop Member: </label>
                    <select
                        value={shopMemberUserId}
                        onChange={(e) => setShopMemberUserId(e.target.value)}
                        className="shop-member-dropdown"
                    >
                        <option value="" disabled>
                            -- Select Shop Member --
                        </option>
                        {shopMembers.map(member => (
                            <option key={member._id} value={member._id}>
                                {member.fullName ? member.fullName : "No Name"}, PH: +91 {member.phone}
                            </option>
                        ))}
                    </select>

                    {shopMemberUserId && (
                        <button onClick={() => setShopMemberUserId("")} className="clear-button">
                            Clear Selection
                        </button>
                    )}
                </div>
            )}

            <div className="announcement-container">
                {(permissions.canCreate || permissions.canUpdate) && (
                <div className="announcement-form-container">
                    <h2>{formMode === 'create' ? 'Create New Announcement' : 'Edit Announcement'}</h2>
                    <form onSubmit={handleSubmit} className="announcement-form">
                        <div className="form-group">
                            <label>Title</label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                required
                                placeholder="Announcement Title"
                            />
                        </div>

                        <div className="form-group">
                            <label>Type</label>
                            <select
                                name="type"
                                value={formData.type}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="text">Text</option>
                                <option value="image">Image</option>
                                <option value="video">Video</option>
                            </select>
                        </div>

                        {formData.type === 'text' && (
                            <div className="form-group">
                                <label>Text Content</label>
                                <textarea
                                    name="text"
                                    value={formData.text}
                                    onChange={handleInputChange}
                                    required
                                    rows="4"
                                    placeholder="Enter announcement text content"
                                ></textarea>
                            </div>
                        )}

                        {formData.type === 'image' && (
                            <div className="form-group">
                                <label>Image</label>
                                <input
                                    type="file"
                                    ref={imageInputRef}
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                />
                                <div className="file-upload-container">
                                    <input
                                        type="text"
                                        name="url"
                                        value={formData.url}
                                        onChange={handleInputChange}
                                        placeholder="upload automatically (select image)"
                                        readOnly
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleUploadClick('image')}
                                        className="upload-btn"
                                        disabled={uploadingFile}
                                    >
                                        {uploadingFile ? 'Uploading...' : 'Upload Image'}
                                    </button>
                                </div>
                                {previewUrl && (
                                    <div className="preview-container">
                                        <img src={previewUrl} alt="Preview" className="file-preview" />
                                    </div>
                                )}
                            </div>
                        )}

                        {formData.type === 'video' && (
                            <div className="form-group">
                                <label>Video</label>
                                <input
                                    type="file"
                                    ref={videoInputRef}
                                    accept="video/*"
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                />
                                <div className="file-upload-container">
                                    <input
                                        type="text"
                                        name="url"
                                        value={formData.url}
                                        onChange={handleInputChange}
                                        placeholder="upload automatically (select video)"
                                        readOnly
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleUploadClick('video')}
                                        className="upload-btn"
                                        disabled={uploadingFile}
                                    >
                                        {uploadingFile ? 'Uploading...' : 'Upload Video'}
                                    </button>
                                </div>
                                {previewUrl && formData.type === 'video' && (
                                    <div className="preview-container">
                                        <video controls className="file-preview">
                                            <source src={previewUrl} type="video/mp4" />
                                            Your browser does not support the video tag.
                                        </video>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="form-group checkbox">
                            <input
                                type="checkbox"
                                name="forAll"
                                checked={formData.forAll}
                                onChange={handleInputChange}
                                id="forAll"
                            />
                            <label htmlFor="forAll">
                                {mode === 'KDP' ? 'Available for All Shops' :
                                    mode === 'KD' ? 'Available for All Users' :
                                        mode === 'DELIVERY' ? 'Available for All Delivery Boys' :
                                            mode === 'SHOP_MEMBERS' ? 'Available for All Shop Members' :
                                                'Available for All'
                                }
                            </label>
                        </div>

                        {!formData.forAll && (
                            <div className="form-group">
                                <label>
                                    {mode === 'KDP' ? 'Shop' :
                                        mode === 'KD' ? 'User' :
                                            mode === 'DELIVERY' ? 'Delivery Boy' :
                                                mode === 'SHOP_MEMBERS' ? 'Shop Member' :
                                                    'Target'
                                    }
                                </label>
                                {mode === 'KDP' ? (
                                    <select
                                        name="shopId"
                                        value={formData.shopId || shopId}
                                        onChange={handleInputChange}
                                        disabled={formData.forAll}
                                    >
                                        {shops.map(shop => (
                                            <option key={shop._id} value={shop._id}>{shop.name}</option>
                                        ))}
                                    </select>
                                ) : mode === 'KD' ? (
                                    <select
                                        name="userId"
                                        value={formData.userId || userId}
                                        onChange={handleInputChange}
                                        disabled={formData.forAll}
                                    >
                                        {users.map(user => (
                                            <option key={user._id} value={user._id}>
                                                {user.fullName ? user.fullName : "No Name"}, PH: +91 {user.phone}
                                            </option>
                                        ))}
                                    </select>
                                ) : mode === 'DELIVERY' ? (
                                    <select
                                        name="deliveryUserId"
                                        value={formData.deliveryUserId || deliveryUserId}
                                        onChange={handleInputChange}
                                        disabled={formData.forAll}
                                    >
                                        {deliveryBoys.map(deliveryBoy => (
                                            <option key={deliveryBoy._id} value={deliveryBoy._id}>
                                                {deliveryBoy.fullName ? deliveryBoy.fullName : "No Name"}, PH: +91 {deliveryBoy.phone}
                                            </option>
                                        ))}
                                    </select>
                                ) : mode === 'SHOP_MEMBERS' ? (
                                    <select
                                        name="userId"
                                        value={formData.userId || shopMemberUserId}
                                        onChange={handleInputChange}
                                        disabled={formData.forAll}
                                    >
                                        {shopMembers.map(member => (
                                            <option key={member._id} value={member._id}>
                                                {member.fullName ? member.fullName : "No Name"}, PH: +91 {member.phone}
                                            </option>
                                        ))}
                                    </select>
                                ) : null
                                }
                            </div>
                        )}

                        <div className="form-group checkbox">
                            <input
                                type="checkbox"
                                name="isActive"
                                checked={formData.isActive}
                                onChange={handleInputChange}
                                id="isActive"
                            />
                            <label htmlFor="isActive">Active</label>
                        </div>

                        <div className="form-actions">
                            <button 
                                type="submit" 
                                className="btn-primary"
                                disabled={formMode === 'create' ? !permissions.canCreate : !permissions.canUpdate}
                            >
                                {formMode === 'create' ? 'Create Announcement' : 'Update Announcement'}
                            </button>
                            {formMode === 'edit' && (
                                <button type="button" className="btn-secondary" onClick={resetForm}>
                                    Cancel
                                </button>
                            )}
                        </div>
                    </form>
                </div>
                )}

                <div className="announcements-list-container">
                    <h2>Current Announcements</h2>
                    {loading ? (
                        <div className="loading">Loading announcements...</div>
                    ) : announcements.length === 0 ? (
                        <div className="no-data">No announcements found</div>
                    ) : (
                        <>
                            {globalAnnouncements.length > 0 && (
                                <div className="announcements-section">
                                    <h3>
                                        {mode === 'KDP' ? 'Global Announcements (For All Shops)' :
                                            mode === 'KD' ? 'Global Announcements (For All Users)' :
                                                mode === 'DELIVERY' ? 'Global Announcements (For All Delivery Boys)' :
                                                    mode === 'SHOP_MEMBERS' ? 'Global Announcements (For All Shop Members)' :
                                                        'Global Announcements'}
                                    </h3>
                                    <div className="announcements-list">
                                        {globalAnnouncements.map((announcement) => (
                                            <div
                                                key={announcement._id}
                                                className={`announcement-item ${!announcement.isActive ? 'inactive' : ''} global-announcement`}
                                            >
                                                <div className="announcement-header">
                                                    <div className="announcement-title">{announcement.title}</div>
                                                    <div className="announcement-status">
                                                        <span className="for-all-badge">For All</span>
                                                        <span className={`status-badge ${announcement.isActive ? 'active' : 'inactive'}`}>
                                                            {announcement.isActive ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="announcement-subheader">
                                                    <div className="announcement-type">{announcement.type}</div>
                                                    <div className="announcement-date">
                                                        {new Date(announcement.createdAt).toLocaleDateString()}
                                                    </div>
                                                </div>

                                                <div className="announcement-content">
                                                    {announcement.type === 'text' && <p>{announcement.text}</p>}
                                                    {announcement.type === 'image' && (
                                                        <div className="media-preview">
                                                            <img src={announcement.url} alt={announcement.title} />
                                                        </div>
                                                    )}
                                                    {announcement.type === 'video' && (
                                                        <div className="media-preview">
                                                            <video controls className="video-preview">
                                                                <source src={announcement.url} type="video/mp4" />
                                                                Your browser does not support the video tag.
                                                            </video>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="announcement-actions">
                                                    {permissions.canUpdate && (
                                                        <button
                                                            className="btn-icon"
                                                            onClick={() => handleEdit(announcement)}
                                                            title="Edit"
                                                        >
                                                            ✏️
                                                        </button>
                                                    )}
                                                    {permissions.canUpdate && (
                                                        <button
                                                            className="btn-icon"
                                                            onClick={() => handleToggleActive(announcement._id, announcement.isActive)}
                                                            title={announcement.isActive ? 'Deactivate' : 'Activate'}
                                                        >
                                                            {announcement.isActive ? '🔴' : '🟢'}
                                                        </button>
                                                    )}
                                                    {permissions.canDelete && (
                                                        <button
                                                            className="btn-icon"
                                                            onClick={() => handleDelete(announcement._id)}
                                                            title="Delete"
                                                        >
                                                            🗑️
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {specificAnnouncements.length > 0 && (
                                <div className="announcements-section">
                                    <h3>
                                        {mode === 'KDP' ? 'Shop-Specific Announcements' :
                                            mode === 'KD' ? 'User-Specific Announcements' :
                                                mode === 'DELIVERY' ? 'Delivery Boy-Specific Announcements' :
                                                    mode === 'SHOP_MEMBERS' ? 'Shop Member-Specific Announcements' :
                                                        'Specific Announcements'}
                                    </h3>
                                    <div className="announcements-list">
                                        {specificAnnouncements.map((announcement) => (
                                            <div
                                                key={announcement._id}
                                                className={`announcement-item ${!announcement.isActive ? 'inactive' : ''}`}
                                            >
                                                <div className="announcement-header">
                                                    <div className="announcement-title">{announcement.title}</div>
                                                    <div className="announcement-status">
                                                        <span className={`status-badge ${announcement.isActive ? 'active' : 'inactive'}`}>
                                                            {announcement.isActive ? 'Active' : 'Inactive'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="announcement-subheader">
                                                    <div className="announcement-type">{announcement.type}</div>
                                                    <div className="announcement-date">
                                                        {new Date(announcement.createdAt).toLocaleDateString()}
                                                    </div>
                                                </div>

                                                <div className="announcement-content">
                                                    {announcement.type === 'text' && <p>{announcement.text}</p>}
                                                    {announcement.type === 'image' && (
                                                        <div className="media-preview">
                                                            <img src={announcement.url} alt={announcement.title} />
                                                        </div>
                                                    )}
                                                    {announcement.type === 'video' && (
                                                        <div className="media-preview">
                                                            <video controls className="video-preview">
                                                                <source src={announcement.url} type="video/mp4" />
                                                                Your browser does not support the video tag.
                                                            </video>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="announcement-actions">
                                                    {permissions.canUpdate && (
                                                        <button
                                                            className="btn-icon"
                                                            onClick={() => handleEdit(announcement)}
                                                            title="Edit"
                                                        >
                                                            ✏️
                                                        </button>
                                                    )}
                                                    {permissions.canUpdate && (
                                                        <button
                                                            className="btn-icon"
                                                            onClick={() => handleToggleActive(announcement._id, announcement.isActive)}
                                                            title={announcement.isActive ? 'Deactivate' : 'Activate'}
                                                        >
                                                            {announcement.isActive ? '🔴' : '🟢'}
                                                        </button>
                                                    )}
                                                    {permissions.canDelete && (
                                                        <button
                                                            className="btn-icon"
                                                            onClick={() => handleDelete(announcement._id)}
                                                            title="Delete"
                                                        >
                                                            🗑️
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AnnouncementPage;